import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, CircularProgress, Snackbar, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CommentIcon from '@mui/icons-material/Comment';
import CampaignIcon from '@mui/icons-material/Campaign';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../../../services/api';

import logger from '../../../../utils/logger';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';
const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  maxWidth: '800px',
  margin: '0 auto',
  marginTop: theme.spacing(3),
}));

const EditLead = () => {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const { isBDM, isTelly, employeeData } = useAdminRole();
  const [formState, setFormState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [bdmList, setBdmList] = useState([]);
  const [bdmLoading, setBdmLoading] = useState(false);
  const [tellyList, setTellyList] = useState([]);
  const [tellyLoading, setTellyLoading] = useState(false);
  const selfSelectLabel = employeeData?.name ? `Self (You – ${employeeData.name})` : 'Self (You)';
  const showSelfBdmOption = isBDM && employeeData?.id;
  const showSelfTellyOption = isTelly && employeeData?.id;

  // Fetch BDM list on component mount
  useEffect(() => {
    const fetchBdmList = async () => {
      setBdmLoading(true);
      try {
        const response = await ApiService.get('/admin/employee/get-bdm');
        const data = response?.data?.data || response?.data || response || [];
        const list = Array.isArray(data) ? data : [];
        setBdmList(list);
      } catch (err) {
        logger.error('Error fetching BDM list:', err);
        setBdmList([]);
      } finally {
        setBdmLoading(false);
      }
    };
    fetchBdmList();
  }, []);

  // Fetch Telly list on component mount
  useEffect(() => {
    const fetchTellyList = async () => {
      setTellyLoading(true);
      try {
        const response = await ApiService.get('/admin/employee/get-telly-employees');
        const data = response?.data?.data || response?.data || response || [];
        const list = Array.isArray(data) ? data : [];
        setTellyList(list);
      } catch (err) {
        logger.error('Error fetching Telly list:', err);
        setTellyList([]);
      } finally {
        setTellyLoading(false);
      }
    };
    fetchTellyList();
  }, []);

  useEffect(() => {
    const fetchLead = async () => {
      setError(null);
      try {
        // Attempt to fetch a single lead by ID. Support both /get-leads/:id and /get-leads?leadId=:id shapes.
        let data = await ApiService.get(`/admin/leads/get-leads/${leadId}`);
        const root = data?.data ?? data;
        let lead = root?.lead || root?.data || root; // common single resource keys
        if (Array.isArray(root?.leads)) {
          lead = root.leads.find(l => String(l.id) === String(leadId)) || root.leads[0];
        }

        if (!lead || typeof lead !== 'object') {
          // fallback: try query style
          data = await ApiService.get(`/admin/leads/get-leads?leadId=${leadId}`);
          const altRoot = data?.data ?? data;
          lead = altRoot?.lead || altRoot?.data || (Array.isArray(altRoot?.leads) ? altRoot.leads[0] : null);
        }

        if (!lead) throw new Error('Lead not found');

        // Map backend fields into UI form state
        const mapped = {
          personName: lead.contact_person || '',
          businessName: lead.business_name || '',
          email: lead.email || '',
          website: lead.website || '',
          contactNumber: lead.phone_number || '',
          altContactNumber: lead.alt_number || '',
          clientType: lead.type || '',
          employee_id: lead.employee_id || null,
          telly_employee_id: lead.telly_employee_id || null,
          is_gmb_services: lead.is_gmb_services || false,
          address: lead.address_line1 || '',
          city: lead.city || '',
          zipcode: lead.zip_code || '',
          state: lead.state || '',
          country: lead.country || '',
          comment: lead.comment || '',
          leadSource: lead.lead_source || '',
          whatsapp: lead.whatsapp || '',
          cro_name: lead.cro_name || '',
          bdm_name: lead.bdm_name || '',
          package: lead.package || '',
          welcome_call: !!lead.welcome_call,
          gmb_account_id: lead.gmb_account_id || '',
        };
        setFormState(mapped);
      } catch (e) {
        logger.error('Failed to load lead details', e);
        setError('Failed to load lead details');
        // Initialize empty form so user can still interact if desired
        setFormState({
          personName: '',
          businessName: '',
          email: '',
          website: '',
          contactNumber: '',
          altContactNumber: '',
          clientType: '',
          employee_id: null,
          telly_employee_id: null,
          is_gmb_services: false,
          address: '',
          city: '',
          zipcode: '',
          state: '',
          country: '',
          comment: '',
          leadSource: '',
          whatsapp: '',
          cro_name: '',
          bdm_name: '',
          package: '',
          welcome_call: false,
          gmb_account_id: '',
        });
      }
    };
    fetchLead();
  }, [leadId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle GMB checkbox
  const handleGmbCheckbox = (e) => {
    setFormState(prev => ({ ...prev, is_gmb_services: e.target.checked }));
  };

  // Fetch location details from pincode using India Post API
  const fetchLocationFromPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;

    setPincodeLoading(true);
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();

      if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setFormState(prev => ({
          ...prev,
          city: postOffice.District || postOffice.Division || '',
          state: postOffice.State || '',
          country: postOffice.Country || 'India',
        }));
      }
    } catch (err) {
      logger.error('Error fetching pincode details:', err);
    } finally {
      setPincodeLoading(false);
    }
  };

  // Handle pincode change with auto-fetch
  const handlePincodeChange = (e) => {
    const pincode = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setFormState(prev => ({ ...prev, zipcode: pincode }));

    if (pincode.length === 6) {
      fetchLocationFromPincode(pincode);
    }
  };

  const handleSave = async () => {
    if (!formState) return;
    setSaving(true);
    setError(null);
    try {
      // Map UI state back to backend payload
      const payload = {
        contact_person: formState.personName || null,
        business_name: formState.businessName || null,
        email: formState.email || null,
        website: formState.website || null,
        phone_number: formState.contactNumber || null,
        alt_number: formState.altContactNumber || null,
        type: formState.clientType || null,
        employee_id: formState.employee_id || null,
        telly_employee_id: formState.telly_employee_id || null,
        is_gmb_services: formState.is_gmb_services,
        is_website_service: false,
        is_smo_services: false,
        is_other_services: false,
        address_line1: formState.address || null,
        city: formState.city || null,
        state: formState.state || null,
        zip_code: formState.zipcode || null,
        country: formState.country || null,
        comment: formState.comment || null,
        lead_source: formState.leadSource || null,
        whatsapp: formState.whatsapp || null,
        cro_name: formState.cro_name || null,
        bdm_name: formState.bdm_name || null,
        package: formState.package || null,
        welcome_call: formState.welcome_call,
        gmb_account_id: formState.gmb_account_id || null,
      };

      await ApiService.put(`/admin/leads/update-leads/${leadId}`, payload);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/admin-dashboard/leads');
      }, 1500);
    } catch (e) {
      logger.error('Failed to update lead', e);
      setError(e?.message || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  if (!formState) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container>
      {/* Beautiful Header with Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
          }}
        >
          <EditIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>Edit Lead</Typography>
          <Typography variant="body2" color="text.secondary">
            Update lead information and keep your records accurate.
          </Typography>
        </Box>
      </Box>
      <FormContainer>
        <Typography variant="h5" fontWeight={600} align="center" gutterBottom>Basic Info</Typography>
        <Divider sx={{ mb: 3 }} />
        {error && (
          <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BusinessIcon sx={{ color: '#6366f1' }} />
          <Typography variant="subtitle1" fontWeight={600}>Business Information</Typography>
        </Box>
        <TextField fullWidth label="Person Name" name="personName" value={formState.personName} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <PersonIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />
        <TextField fullWidth label="Business Name" name="businessName" value={formState.businessName} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <BusinessIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />
        <TextField fullWidth label="Email Address" name="email" type="email" value={formState.email} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <EmailIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />
        <TextField fullWidth label="Website" name="website" value={formState.website} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <PublicIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
          <PhoneIcon sx={{ color: '#6366f1' }} />
          <Typography variant="subtitle1" fontWeight={600}>Contact Information</Typography>
        </Box>
        <TextField fullWidth label="Contact Number" name="contactNumber" value={formState.contactNumber} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <PhoneIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />
        <TextField fullWidth label="Alternative Contact Number" name="altContactNumber" value={formState.altContactNumber} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <PhoneIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Client Type</InputLabel>
          <Select name="clientType" value={formState.clientType} onChange={handleChange}>
            <MenuItem value="Monthly">Monthly</MenuItem>
            <MenuItem value="Quarterly">Quarterly</MenuItem>
            <MenuItem value="Six-Month">Six-Month</MenuItem>
            <MenuItem value="Annually">Annually</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Assign BDM</InputLabel>
          <Select
            name="employee_id"
            value={formState.employee_id || ''}
            label="Assign BDM"
            onChange={handleChange}
            disabled={bdmLoading}
            startAdornment={<PersonIcon sx={{ color: '#9ca3af', mr: 1 }} />}
          >
            <MenuItem value="">
              <em>{bdmLoading ? 'Loading...' : (bdmList.length === 0 ? 'No BDMs available' : 'Select BDM')}</em>
            </MenuItem>
              {showSelfBdmOption && (
                <MenuItem key="bdm-self-select" value={employeeData.id}>
                  {selfSelectLabel}
                </MenuItem>
              )}
            {bdmList.map((bdm) => (
              <MenuItem key={bdm.id} value={bdm.id}>
                {[bdm.first_name, bdm.last_name].filter(Boolean).join(' ') || bdm.email || `Employee #${bdm.id}`}
              </MenuItem>
            ))}
          </Select>
          {bdmList.length === 0 && !bdmLoading && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              Add employees with BDM role in Employee Management to assign here
            </Typography>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Assign Telly</InputLabel>
          <Select
            name="telly_employee_id"
            value={formState.telly_employee_id || ''}
            label="Assign Telly"
            onChange={handleChange}
            disabled={tellyLoading}
            startAdornment={<PhoneIcon sx={{ color: '#9ca3af', mr: 1 }} />}
          >
            <MenuItem value="">
              <em>{tellyLoading ? 'Loading...' : (tellyList.length === 0 ? 'No Telly available' : 'Select Telly')}</em>
            </MenuItem>
              {showSelfTellyOption && (
                <MenuItem key="telly-self-select" value={employeeData.id}>
                  {selfSelectLabel}
                </MenuItem>
              )}
            {tellyList.map((telly) => (
              <MenuItem key={telly.id} value={telly.id}>
                {[telly.first_name, telly.last_name].filter(Boolean).join(' ') || telly.email || `Employee #${telly.id}`}
              </MenuItem>
            ))}
          </Select>
          {tellyList.length === 0 && !tellyLoading && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              Add employees with Telly role in Employee Management to assign here
            </Typography>
          )}
        </FormControl>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Service</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formState.is_gmb_services}
                onChange={handleGmbCheckbox}
              />
            }
            label="GMB (Google My Business)"
          />
        </Box>

        <TextField fullWidth label="Address" name="address" value={formState.address} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <LocationOnIcon sx={{ color: '#9ca3af', mr: 1 }} /> }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <TextField
            label="Pincode"
            name="zipcode"
            value={formState.zipcode}
            onChange={handlePincodeChange}
            placeholder="Enter 6-digit pincode"
            helperText={pincodeLoading ? "Fetching location..." : "City, State & Country will auto-fill"}
            InputProps={{
              endAdornment: pincodeLoading ? <CircularProgress size={20} /> : null,
            }}
          />
          <TextField
            label="City"
            name="city"
            value={formState.city}
            onChange={handleChange}
            disabled={pincodeLoading}
          />
          <TextField
            label="State"
            name="state"
            value={formState.state}
            onChange={handleChange}
            disabled={pincodeLoading}
          />
          <TextField
            label="Country"
            name="country"
            value={formState.country}
            onChange={handleChange}
            disabled={pincodeLoading}
          />
        </Box>
        <TextField fullWidth label="Comment" name="comment" multiline rows={3} value={formState.comment} onChange={handleChange} sx={{ mb: 2 }} InputProps={{ startAdornment: <CommentIcon sx={{ color: '#9ca3af', mr: 1, alignSelf: 'flex-start', mt: 1.5 }} /> }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Lead Source</InputLabel>
          <Select name="leadSource" value={formState.leadSource} onChange={handleChange} startAdornment={<CampaignIcon sx={{ color: '#9ca3af', mr: 1 }} />}>
            <MenuItem value="Instagram">Instagram</MenuItem>
            <MenuItem value="Facebook">Facebook</MenuItem>
            <MenuItem value="Google">Google</MenuItem>
            <MenuItem value="Referral">Referral</MenuItem>
            <MenuItem value="Advertisement">Advertisement</MenuItem>
            <MenuItem value="Website">Website</MenuItem>
            <MenuItem value="Cold Call">Cold Call</MenuItem>
            <MenuItem value="WhatsApp">WhatsApp</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Closure Information</Typography>

        <TextField fullWidth label="WhatsApp" name="whatsapp" value={formState.whatsapp} onChange={handleChange} sx={{ mb: 2 }} />
        <TextField fullWidth label="CRO Name" name="cro_name" value={formState.cro_name} onChange={handleChange} sx={{ mb: 2 }} />
        <TextField fullWidth label="BDM Name (Manual override)" name="bdm_name" value={formState.bdm_name} onChange={handleChange} sx={{ mb: 2 }} />
        <TextField fullWidth label="GMB Account ID" name="gmb_account_id" value={formState.gmb_account_id} onChange={handleChange} sx={{ mb: 2 }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Selected Plan</InputLabel>
          <Select name="package" value={formState.package} onChange={handleChange} label="Selected Plan">
            <MenuItem value=""><em>None</em></MenuItem>
            <MenuItem value="basic">Basic Plan</MenuItem>
            <MenuItem value="advance">Advance Plan</MenuItem>
            <MenuItem value="pro">Pro Plan</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Checkbox checked={formState.welcome_call} onChange={handleChange} name="welcome_call" color="success" />}
          label="Welcome Call Completed"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '8px',
              px: 4,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              borderRadius: '8px',
              px: 4,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5558e8 0%, #7c4fe3 100%)',
                boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </FormContainer>

      {/* Success Toast Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: '#10B981',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
            minWidth: '280px',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Lead Updated Successfully!</Typography>
            <Typography sx={{ fontSize: '12px', opacity: 0.9 }}>Redirecting to dashboard...</Typography>
          </Box>
        </Box>
      </Snackbar>
    </Container>
  );
};

export default EditLead;
